<template>
  <div class="catalog-page">
    <!-- Navigation Button -->
    <button @click="$emit('navigate', 'taxonomyEditor')" class="nav-button">
      Open Taxonomy Editor
    </button>

    <!-- Catalog Layout -->
    <div class="catalog-layout">
      <!-- Filter Panel -->
      <div class="filters-sidebar">
        <!-- Brand Filter -->
        <div class="filter-section">
          <h3>Brand</h3>
          <div class="search-box">
            <input 
              type="text" 
              v-model="brandSearch" 
              placeholder="Search brands..."
              @input="filterBrands"
            />
          </div>
          <div class="filter-options">
            <label v-for="brand in filteredBrands" :key="brand" class="filter-option">
              <input
                type="checkbox"
                :value="brand"
                v-model="selectedFilters.brands"
                @change="applyFilters"
              />
              <span>{{ brand }}</span>
            </label>
          </div>
        </div>

        <!-- Attribute Filters -->
        <div class="filter-section">
          <h3>Attribute Filters</h3>
          <!-- Attribute Filter Type -->
          <div class="filter-field">
            <label for="attribute-filter-type">Attribute Filter Type:</label>
            <select
              v-model="selectedAttributeFilter.type"
              @change="onAttributeFilterTypeChange"
              id="attribute-filter-type"
              class="filter-select"
            >
              <option value="">Select Filter Type</option>
              <option value="dressCategory">Dress Category</option>
              <option value="manorTaxonomy">Manor Taxonomy Attributes</option>
              <option value="alliumTaxonomy">AlliumAI Taxonomy Attributes</option>
            </select>
          </div>

          <!-- Attribute Name -->
          <div v-if="selectedAttributeFilter.type" class="filter-field">
            <label for="attribute-name">Attribute Name:</label>
            <select
              v-model="selectedAttributeFilter.attribute"
              @change="onAttributeNameChange"
              id="attribute-name"
              class="filter-select"
            >
              <option value="">Select Attribute</option>
              <option
                v-for="attrName in getAttributeNames"
                :key="attrName"
                :value="attrName"
              >
                {{ attrName }}
              </option>
            </select>
          </div>

          <!-- Attribute Value -->
          <div v-if="selectedAttributeFilter.attribute" class="filter-field">
            <label for="attribute-value">Attribute Value:</label>
            <select
              v-model="selectedAttributeFilter.value"
              @change="applyFilters"
              id="attribute-value"
              class="filter-select"
            >
              <option value="">Select Value</option>
              <option
                v-for="attrValue in getFilterValues"
                :key="attrValue"
                :value="attrValue"
              >
                {{ attrValue }}
              </option>
            </select>
          </div>
        </div>

        <!-- Flagged Attribute Filter -->
        <div class="filter-section">
          <h3>Flagged Attributes</h3>
          <div class="filter-field">
            <label for="flagged-attribute">Select Flagged Attribute:</label>
            <select
              v-model="selectedFlaggedAttribute"
              @change="applyFilters"
              id="flagged-attribute"
              class="filter-select"
            >
              <option value="">Select Attribute</option>
              <option
                v-for="attr in flaggedAttributesList"
                :key="attr"
                :value="attr"
              >
                {{ formatOptionLabel(attr) }}
              </option>
            </select>
          </div>
        </div>

        <!-- Active Filters Summary -->
        <div class="active-filters" v-if="hasActiveFilters">
          <h3>Active Filters</h3>
          <div class="filter-tags">
            <div 
              v-for="(filters, category) in activeFilters" 
              :key="category"
              class="filter-category"
            >
              <div 
                v-for="filter in filters" 
                :key="`${category}-${filter}`" 
                class="filter-tag"
              >
                {{ filter }}
                <button @click="removeFilter(category, filter)" class="remove-filter">
                  ×
                </button>
              </div>
            </div>
          </div>
          <button class="clear-filters" @click="clearFilters">Clear All Filters</button>
        </div>
      </div>

      <!-- Products Grid -->
      <div class="products-content">
        <!-- Results Summary -->
        <div class="results-summary">
          Showing {{ displayedProducts.length }} of {{ filteredProducts.length }} products
        </div>

        <div class="products-grid">
          <div
            v-for="product in displayedProducts"
            :key="`${product.productId}-${product.variantId}`"
            class="product-card"
            @click="openProductPage(product.productId, product.variantId)"
          >
            <div class="product-image">
              <img :src="product.image" :alt="product.title" @error="onImageError" />
            </div>
            <div class="product-info">
              <h3>{{ product.title }}</h3>
              <p class="product-brand">{{ product.brand }}</p>
              <p class="product-category">{{ product.dressCategory }}</p>
            </div>
          </div>
        </div>

        <!-- Loading Indicator or All Products Loaded Message -->
        <div v-if="isLoading" class="loading-indicator">
          <div class="loading-spinner"></div>
          <span>Loading more products...</span>
        </div>
        <div v-else-if="!hasMore && hasActiveFilters" class="all-loaded-message">
          <span>All products loaded.</span>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
export default {
  name: 'Catalog',
  props: {
    products: {
      type: Object,
      required: true,
    },
    brands: {
      type: Array,
      default: () => [],
    },
    dressCategories: {
      type: Array,
      default: () => [],
    },
    manorTaxonomyAttributes: {
      type: Object,
      default: () => ({
        attributes: [],
        values: {}
      }),
    },
    alliumTaxonomyAttributes: {
      type: Object,
      default: () => ({
        attributes: [],
        values: {}
      }),
    },
    selectedLanguage: {
      type: String,
      default: 'english',
    },
    flaggedAttributesList: {
      type: Array,
      default: () => [],
    },
    selectedFlaggingStrategy: {
      type: String,
      default: 'None',
    },
  },
  data() {
    return {
      showTaxonomyEditor: false,
      pageSize: 20,
      currentPage: 1,
      displayedProducts: [],
      isLoading: false,
      hasMore: true, // New Flag
      brandSearch: '',
      attributeSearch: {},
      selectedFilters: {
        brands: [],
        categories: [],
        manorAttributes: {},
        alliumAttributes: {},
      },
      filteredBrands: [],
      selectedAttributeFilter: {
        type: '',
        attribute: '',
        value: ''
      },
      attributeFilters: {
        dressCategory: {
          attributes: [],
          values: {},
        },
        manorTaxonomy: {
          attributes: [],
          values: {},
        },
        alliumTaxonomy: {
          attributes: [],
          values: {},
        }
      },
      selectedFlaggedAttribute: '', // New data property
    };
  },
  computed: {
    hasActiveFilters() {
      return (
        this.selectedFilters.brands.length > 0 ||
        this.selectedFilters.categories.length > 0 ||
        Object.values(this.selectedFilters.manorAttributes).some(arr => arr.length > 0) ||
        Object.values(this.selectedFilters.alliumAttributes).some(arr => arr.length > 0) ||
        (this.selectedAttributeFilter.type && this.selectedAttributeFilter.value) ||
        this.selectedFlaggedAttribute // Include flagged attribute filter
      );
    },
    getAttributeNames() {
      if (!this.selectedAttributeFilter.type) {
        return [];
      }
      switch (this.selectedAttributeFilter.type) {
        case 'dressCategory':
          return ['ClothingType'];
        case 'manorTaxonomy':
          return this.manorTaxonomyAttributes.attributes || [];
        case 'alliumTaxonomy':
          return this.alliumTaxonomyAttributes.attributes || [];
        default:
          return [];
      }
    },
    activeFilters() {
      const filters = {};

      if (this.selectedFilters.brands.length) {
        filters.Brands = this.selectedFilters.brands;
      }

      if (this.selectedFilters.categories.length) {
        filters.Categories = this.selectedFilters.categories;
      }

      Object.entries(this.selectedFilters.manorAttributes).forEach(([key, values]) => {
        if (values.length) {
          filters[key] = values;
        }
      });

      Object.entries(this.selectedFilters.alliumAttributes).forEach(([key, values]) => {
        if (values.length) {
          filters[key] = values;
        }
      });

      if (this.selectedFlaggedAttribute) {
        filters['Flagged Attribute'] = [this.selectedFlaggedAttribute];
      }

      return filters;
    },
    filteredProducts() {
      return this.getAllProducts().filter(product => {
        // Apply brand filter
        if (this.selectedFilters.brands.length && 
            !this.selectedFilters.brands.includes(product.brand)) {
          return false;
        }

        // Apply attribute filter
        if (this.selectedAttributeFilter.type && 
            this.selectedAttributeFilter.attribute && 
            this.selectedAttributeFilter.value) {
          const value = this.getAttributeValue(product, 
            this.selectedAttributeFilter.type,
            this.selectedAttributeFilter.attribute);
          if (value !== this.selectedAttributeFilter.value) {
            return false;
          }
        }

        // Apply flagged attribute filter
        if (this.selectedFlaggedAttribute) {
          const afterData = this.products[product.productId]?.[product.variantId]?.after?.[this.selectedLanguage];
          if (!afterData || !afterData.predictions_flagged) return false;

          const predictionsFlagged = afterData.predictions_flagged;

          // Check in both AlliumAI and Manor attributes
          const alliumFlaggedAttributes = predictionsFlagged.attributes?.[this.selectedFlaggingStrategy];
          const manorFlaggedAttributes = predictionsFlagged.manor_taxonomy_attributes?.[this.selectedFlaggingStrategy];

          if (
            !(
              (alliumFlaggedAttributes && alliumFlaggedAttributes[this.selectedFlaggedAttribute]) ||
              (manorFlaggedAttributes && manorFlaggedAttributes[this.selectedFlaggedAttribute])
            )
          ) {
            return false;
          }
        }

        return true;
      });
    },
    getFilterValues() {
      if (!this.selectedAttributeFilter.type || !this.selectedAttributeFilter.attribute) {
        return [];
      }
      
      const values = this.attributeFilters[this.selectedAttributeFilter.type]?.values[this.selectedAttributeFilter.attribute];
      if (!values) {
        console.warn('No values found for', {
          type: this.selectedAttributeFilter.type,
          attribute: this.selectedAttributeFilter.attribute,
          availableValues: this.attributeFilters[this.selectedAttributeFilter.type]?.values
        });
        return [];
      }
      return values;
    }
  },
  watch: {
    products: {
      handler() {
        console.log('Products changed, rebuilding filters and applying filters');
        this.buildAttributeFilters();
        this.applyFilters();
      },
      immediate: true,
      deep: true,
    },
    manorTaxonomyAttributes: {
      handler(newVal) {
        this.attributeFilters.manorTaxonomy = {
          attributes: newVal.attributes || [],
          values: newVal.values || {}
        };
        console.log('Updated manor taxonomy attributes:', this.attributeFilters.manorTaxonomy);
      },
      immediate: true,
      deep: true
    },
    alliumTaxonomyAttributes: {
      handler(newVal) {
        this.attributeFilters.alliumTaxonomy = {
          attributes: newVal.attributes || [],
          values: newVal.values || {}
        };
        console.log('Updated allium taxonomy attributes:', this.attributeFilters.alliumTaxonomy);
      },
      immediate: true,
      deep: true
    },
    dressCategories: {
      handler(newVal) {
        this.attributeFilters.dressCategory.values['Dress Category'] = newVal;
        console.log('Updated dress categories:', this.attributeFilters.dressCategory);
      },
      immediate: true
    }
  },
  methods: {
    getAllProducts() {
      const productsArray = [];
      for (const productId in this.products) {
        const variants = this.products[productId];
        for (const variantId in variants) {
          const variant = variants[variantId];
          const afterData = variant.after?.[this.selectedLanguage];
          if (afterData) {
            const productTitle =
              afterData.productTitle ||
              afterData['Product Title'] ||
              'No title';
            const image =
              variant.image_urls?.[0] ||
              variant.before?.image_urls?.[0] ||
              'https://via.placeholder.com/200x200?text=No+Image';

            // Get ClothingType from manor_taxonomy_attributes
            const clothingType = afterData.manor_taxonomy_attributes?.ClothingType || 'Unknown';

            productsArray.push({
              productId,
              variantId,
              title: productTitle,
              image,
              brand: variant.Brand || 'Unknown',
              dressCategory: clothingType, // Use ClothingType for the category display
              manorTaxonomyAttributes: afterData.manor_taxonomy_attributes || {},
              alliumTaxonomyAttributes: afterData.attributes || {},
            });
          }
        }
      }
      return productsArray;
    },

    // Update getAttributeValue to handle both cases
    getAttributeValue(product, type, attribute) {
      const afterData = this.products[product.productId]?.[product.variantId]?.after?.[this.selectedLanguage];
      
      switch (type) {
        case 'dressCategory':
          return afterData?.manor_taxonomy_attributes?.ClothingType || 'Unknown';
        case 'manorTaxonomy':
          return product.manorTaxonomyAttributes[attribute];
        case 'alliumTaxonomy':
          return product.alliumTaxonomyAttributes[attribute];
        default:
          return null;
      }
    },

    onAttributeFilterTypeChange() {
      this.selectedAttributeFilter.attribute = '';
      this.selectedAttributeFilter.value = '';
      console.log('Filter type changed. Available attributes:', this.getAttributeNames);
      this.applyFilters();
    },

    onAttributeNameChange() {
      this.selectedAttributeFilter.value = '';
      console.log('Attribute changed. Available values:', this.getFilterValues);
      this.applyFilters();
    },

    buildAttributeFilters() {
      try {
        // Initialize filter structure
        this.attributeFilters = {
          dressCategory: {
            attributes: ['ClothingType'], // Changed to match Manor case
            values: { 'ClothingType': new Set() }, // Changed to match Manor case
          },
          manorTaxonomy: {
            attributes: [],
            values: {},
          },
          alliumTaxonomy: {
            attributes: [],
            values: {},
          },
        };

        const productsArray = this.getAllProducts();
        
        productsArray.forEach((product) => {
          const afterData = this.products[product.productId]?.[product.variantId]?.after?.[this.selectedLanguage];
          if (!afterData) return;

          // Get clothing type from manor_taxonomy_attributes
          const clothingType = afterData.manor_taxonomy_attributes?.ClothingType;
          
          // If we have a valid clothing type, add it to both filter categories
          if (clothingType && clothingType !== 'Unknown') {
            // Add to Dress Category filter
            this.attributeFilters.dressCategory.values['ClothingType'].add(clothingType);
            
            // Add to Manor taxonomy filter
            if (!this.attributeFilters.manorTaxonomy.attributes.includes('ClothingType')) {
              this.attributeFilters.manorTaxonomy.attributes.push('ClothingType');
              this.attributeFilters.manorTaxonomy.values['ClothingType'] = new Set();
            }
            this.attributeFilters.manorTaxonomy.values['ClothingType'].add(clothingType);
          }

          // Process remaining Manor Taxonomy attributes
          if (afterData.manor_taxonomy_attributes) {
            Object.entries(afterData.manor_taxonomy_attributes).forEach(([key, value]) => {
              if (value && typeof value === 'string') {
                if (!this.attributeFilters.manorTaxonomy.attributes.includes(key)) {
                  this.attributeFilters.manorTaxonomy.attributes.push(key);
                  this.attributeFilters.manorTaxonomy.values[key] = new Set();
                }
                this.attributeFilters.manorTaxonomy.values[key].add(value);
              }
            });
          }

          // Process Allium Taxonomy attributes
          if (afterData.attributes) {
            Object.entries(afterData.attributes).forEach(([key, value]) => {
              if (value && typeof value === 'string') {
                if (!this.attributeFilters.alliumTaxonomy.attributes.includes(key)) {
                  this.attributeFilters.alliumTaxonomy.attributes.push(key);
                  this.attributeFilters.alliumTaxonomy.values[key] = new Set();
                }
                this.attributeFilters.alliumTaxonomy.values[key].add(value);
              }
            });
          }
        });

        // Convert Sets to sorted Arrays
        for (const category in this.attributeFilters) {
          this.attributeFilters[category].attributes.sort();
          for (const attr in this.attributeFilters[category].values) {
            this.attributeFilters[category].values[attr] = 
              Array.from(this.attributeFilters[category].values[attr]).sort();
          }
        }

        console.log('Built filters:', {
          dressCategoryValues: this.attributeFilters.dressCategory.values['ClothingType'],
          manorValues: this.attributeFilters.manorTaxonomy.values['ClothingType']
        });
      } catch (error) {
        console.error('Error building attribute data:', error);
      }
    },

    applyFilters() {
      this.isLoading = true;
      this.displayedProducts = [];
      this.currentPage = 1;

      setTimeout(() => {
        if (!this.hasActiveFilters) {
          // No filters applied, show all products
          this.displayedProducts = this.filteredProducts;
          this.hasMore = false; // All products are displayed
        } else {
          // Filters applied, use pagination
          this.displayedProducts = this.filteredProducts.slice(0, this.pageSize);
          this.hasMore = this.filteredProducts.length > this.pageSize;
        }
        this.isLoading = false;
      }, 500);
    },

    filterProducts() {
      let filtered = this.getAllProducts();

      // Apply brand filter
      if (this.selectedFilters.brands.length > 0) {
        filtered = filtered.filter((product) =>
          this.selectedFilters.brands.includes(product.brand)
        );
      }

      // Apply selected attribute filter
      const { type, attribute, value } = this.selectedAttributeFilter;
      if (type && attribute && value) {
        filtered = filtered.filter((product) => {
          const attrValue = this.getAttributeValue(product, type, attribute);
          return attrValue === value;
        });
      }

      // Apply flagged attribute filter
      if (this.selectedFlaggedAttribute) {
        filtered = filtered.filter((product) => {
          const afterData = this.products[product.productId]?.[product.variantId]?.after?.[this.selectedLanguage];
          if (!afterData || !afterData.predictions_flagged) return false;

          const predictionsFlagged = afterData.predictions_flagged;

          // Check in both AlliumAI and Manor attributes
          const alliumFlaggedAttributes = predictionsFlagged.attributes?.[this.selectedFlaggingStrategy];
          const manorFlaggedAttributes = predictionsFlagged.manor_taxonomy_attributes?.[this.selectedFlaggingStrategy];

          return (
            (alliumFlaggedAttributes && alliumFlaggedAttributes[this.selectedFlaggedAttribute]) ||
            (manorFlaggedAttributes && manorFlaggedAttributes[this.selectedFlaggedAttribute])
          );
        });
      }

      return filtered;
    },

    loadMoreProducts() {
      if (this.isLoading || !this.hasMore) return; // Prevent loading if no more products

      this.isLoading = true;
      setTimeout(() => {
        const startIndex = this.displayedProducts.length;
        const endIndex = startIndex + this.pageSize;
        const nextProducts = this.filteredProducts.slice(startIndex, endIndex);
        
        this.displayedProducts.push(...nextProducts);
        
        // Update hasMore based on remaining products
        if (this.displayedProducts.length >= this.filteredProducts.length) {
          this.hasMore = false;
        }
        this.isLoading = false;
      }, 500);
    },

    clearFilters() {
      this.selectedFilters = {
        brands: [],
        categories: [],
        manorAttributes: {},
        alliumAttributes: {},
      };
      this.selectedAttributeFilter = {
        type: '',
        attribute: '',
        value: '',
      };
      this.brandSearch = '';
      this.attributeSearch = {};
      this.selectedFlaggedAttribute = ''; // Reset flagged attribute filter
      this.hasMore = !this.hasActiveFilters; // Update hasMore accordingly
      this.applyFilters();
    },

    filterBrands() {
      if (!this.brandSearch) {
        this.filteredBrands = this.brands;
        return;
      }
      const search = this.brandSearch.toLowerCase();
      this.filteredBrands = this.brands.filter((brand) =>
        brand.toLowerCase().includes(search)
      );
    },

    onImageError(event) {
      event.target.src =
        'https://via.placeholder.com/200x200?text=Image+Not+Available';
    },

    openProductPage(productId, variantId) {
      this.$emit('open-product', { productId, variantId });
    },

    handleScroll() {
      const bottomOfWindow =
        window.innerHeight + window.scrollY >=
        document.body.offsetHeight - 100;
      if (bottomOfWindow && !this.isLoading && this.hasMore) {
        this.loadMoreProducts();
      }
    },

    isAttributeSelected(type, attribute, value) {
      return (
        this.selectedFilters.attributes[attribute] &&
        this.selectedFilters.attributes[attribute].includes(value)
      );
    },

    toggleAttribute(type, attribute, value) {
      if (!this.selectedFilters.attributes[attribute]) {
        this.selectedFilters.attributes[attribute] = [];
      }
      const index = this.selectedFilters.attributes[attribute].indexOf(
        value
      );
      if (index === -1) {
        this.selectedFilters.attributes[attribute].push(value);
      } else {
        this.selectedFilters.attributes[attribute].splice(index, 1);
        if (
          this.selectedFilters.attributes[attribute].length === 0
        ) {
          delete this.selectedFilters.attributes[attribute];
        }
      }
      this.applyFilters();
    },

    filterAttributeValues(values, attribute) {
      if (!this.attributeSearch[attribute]) {
        return values;
      }
      const search = this.attributeSearch[attribute].toLowerCase();
      return values.filter((value) =>
        value.toLowerCase().includes(search)
      );
    },

    removeFilter(category, value) {
      if (category === 'Brands') {
        this.selectedFilters.brands = this.selectedFilters.brands.filter(
          (b) => b !== value
        );
      } else if (category === 'Flagged Attribute') {
        this.selectedFlaggedAttribute = '';
      } else {
        if (this.selectedFilters.attributes[category]) {
          this.selectedFilters.attributes[category] =
            this.selectedFilters.attributes[category].filter(
              (v) => v !== value
            );
          if (
            this.selectedFilters.attributes[category].length === 0
          ) {
            delete this.selectedFilters.attributes[category];
          }
        }
      }
      this.applyFilters();
    },

    formatOptionLabel(option) {
      return option
        .replace(/_/g, ' ')
        .replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1));
    },
  },

  mounted() {
    this.filteredBrands = this.brands;
    window.addEventListener('scroll', this.handleScroll);
    console.log('Building filters with props:', {
      dressCategories: this.dressCategories,
      manorTaxonomyAttributes: this.manorTaxonomyAttributes,
      alliumTaxonomyAttributes: this.alliumTaxonomyAttributes
    });
    this.buildAttributeFilters();
    this.applyFilters();
  },

  beforeUnmount() { 
    window.removeEventListener('scroll', this.handleScroll);
  },

  created() {
    // Initialize attribute search object
    const allAttributes = [
      ...Object.keys(this.manorTaxonomyAttributes.values || {}),
      ...Object.keys(this.alliumTaxonomyAttributes.values || {})
    ];
    
    this.attributeSearch = {};
    allAttributes.forEach(attr => {
      this.attributeSearch[attr] = '';
    });
    console.log('Initial props:', {
      dressCategories: this.dressCategories,
      manorTaxonomyAttributes: this.manorTaxonomyAttributes,
      alliumTaxonomyAttributes: this.alliumTaxonomyAttributes
    });
  },
}
</script>

<style scoped>
.catalog-page {
  min-height: 100vh;
  width: 100vw;
  background-color: #f9fafb;
}

.catalog-layout {
  display: flex;
  gap: 2rem;
  padding: 20px;
  max-width: 1800px;
  margin: 0 auto;
}

.filters-sidebar {
  width: 300px;
  flex-shrink: 0;
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  height: fit-content;
  position: sticky;
  top: 20px;
  max-height: calc(100vh - 40px);
  overflow-y: auto;
}

.filter-section {
  margin-bottom: 2rem;
}

.filter-section h3 {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #374151;
}

.search-box {
  margin-bottom: 0.75rem;
}

.search-box input {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  font-size: 0.875rem;
}

.search-box input:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
}

.filter-options {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 200px;
  overflow-y: auto;
  padding-right: 0.5rem;
}

.filter-options::-webkit-scrollbar {
  width: 4px;
}

.filter-options::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.filter-options::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}

.filter-option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
  color: #4b5563;
}

.filter-option:hover {
  color: #1f2937;
}

.filter-option input[type="checkbox"] {
  width: 1rem;
  height: 1rem;
  border-radius: 4px;
  border: 1px solid #d1d5db;
  cursor: pointer;
}

.active-filters {
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
}

.filter-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 1rem 0;
}

.filter-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  background-color: #e5e7eb;
  border-radius: 4px;
  font-size: 0.75rem;
  color: #374151;
}

.remove-filter {
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  padding: 0 0.25rem;
}

.remove-filter:hover {
  color: #ef4444;
}

.clear-filters {
  width: 100%;
  padding: 0.5rem;
  background-color: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  color: #374151;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
}

.clear-filters:hover {
  background-color: #e5e7eb;
}

.products-content {
  flex: 1;
  min-width: 0;
}

.results-summary {
  margin-bottom: 1rem;
  font-size: 0.875rem;
  color: #6b7280;
}

.products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.product-card {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s, box-shadow 0.2s;
  cursor: pointer;
  width: 100%;
}

.product-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.product-image {
  position: relative;
  padding-top: 100%;
  background-color: #f3f4f6;
}

.product-image img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-info {
  padding: 1rem;
}

.product-info h3 {
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.5rem;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.product-brand {
  font-size: 0.875rem;
  color: #6b7280;
  margin-bottom: 0.25rem;
}

.product-category {
  font-size: 0.75rem;
  color: #9ca3af;
}

.loading-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 2rem;
  color: #6b7280;
}

.loading-spinner {
  width: 1.5rem;
  height: 1.5rem;
  border: 2px solid #e5e7eb;
  border-top-color: #2563eb;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.filter-field {
  margin-bottom: 1rem;
}

.filter-field label {
  display: block;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  color: #374151;
}

.filter-select {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  font-size: 0.875rem;
  color: #1f2937;
  background-color: white;
}

.filter-select:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Responsive Design */
@media (max-width: 1024px) {
  .catalog-layout {
    gap: 1rem;
  }

  .filters-sidebar {
    width: 250px;
  }
}

@media (max-width: 768px) {
  .catalog-layout {
    flex-direction: column;
  }

  .filters-sidebar {
    width: 100%;
    position: static;
    max-height: none;
    margin-bottom: 1rem;
  }

  .products-grid {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  }
}

@media (max-width: 640px) {
  .catalog-layout {
    padding: 1rem;
  }

  .products-grid {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 1rem;
  }

  .product-info {
    padding: 0.75rem;
  }

  .product-info h3 {
    font-size: 0.875rem;
  }
}
.nav-button {
  margin: 20px;
  padding: 10px 20px;
  background-color: var(--primary-color, #007bff);
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.nav-button:hover {
  background-color: var(--primary-color-dark, #0056b3);
}
</style>